import { useRef, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Nfc, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Saisie d'un code de badge NFC ou RFID, utilisée à deux endroits :
//   - Administration > Utilisateurs (enregistrer le badge d'un employé)
//   - Page de connexion (se connecter en passant son badge)
//
// Deux façons de lire le badge, pour couvrir le matériel réel :
//   1. Lecteur USB branché (le plus courant, RFID comme NFC) : ces lecteurs
//      se comportent comme un CLAVIER — ils "tapent" le numéro du badge très
//      vite, suivi d'Entrée. Un simple champ texte suffit donc à les capter,
//      à condition qu'il soit focus au moment du passage du badge.
//   2. Puce NFC intégrée au téléphone/tablette (Android + Chrome
//      uniquement — l'API Web NFC n'est pas disponible ailleurs) : un
//      bouton "Scanner via NFC" déclenche une lecture native quand
//      disponible, en repli sur la saisie manuelle sinon.
export function BadgeScanInput({ badgeType, value, onChange, onEnter, autoFocus, testIdPrefix = "badge" }) {
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef(null);

  const nfcDisponible = badgeType === "nfc" && typeof window !== "undefined" && "NDEFReader" in window;

  const scannerViaNfc = async () => {
    setScanning(true);
    try {
      // eslint-disable-next-line no-undef
      const reader = new NDEFReader();
      await reader.scan();
      toast.info("Approchez le badge du téléphone...");
      reader.onreading = (event) => {
        const code = event.serialNumber || event.message?.records?.[0]?.data || "";
        if (code) {
          onChange(code.toUpperCase());
          toast.success("Badge détecté");
        }
        setScanning(false);
      };
    } catch (err) {
      toast.error("Lecture NFC impossible sur cet appareil — saisissez le code manuellement.");
      setScanning(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Input
        ref={inputRef}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter" && onEnter) { e.preventDefault(); onEnter(); } }}
        placeholder="Passez le badge ou saisissez manuellement"
        data-testid={`${testIdPrefix}-code-input`}
        className="flex-1"
      />
      {nfcDisponible && (
        <Button type="button" variant="outline" onClick={scannerViaNfc} disabled={scanning} className="gap-2 shrink-0" data-testid={`${testIdPrefix}-nfc-scan-button`}>
          {scanning ? <Loader2 size={15} className="animate-spin" /> : <Nfc size={15} />}
          Scanner via NFC
        </Button>
      )}
    </div>
  );
}
